import {
  AnalyticsEventNameType,
  DataSource,
  DataSourceType,
  LookerAppInfo,
  OauthAttemptStatus,
} from "@coeff/api";
import { DataSourceTypeDisplayNames } from "@coeff/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  useApiClient,
  useEventManagerContext,
  useGasServerContext,
  useMessage,
} from "../../../globals/contexts";
import { QueryCacheKey, useAuthContext } from "../../context/AuthContext";
import {
  getOAuthAuthorizationUrl as getOAuthAuthorizationUrlFallback,
  sentryCapture,
} from "../../utils";

import { formatOuathErrorMessage } from "./AuthorizeLookerContentV2";

interface UseOAuthProps {
  event_props: { email?: string };
  onAuthorize: (dataSource: DataSource) => void;
  onError?: (error: { title: string; message: string }) => void;
}

type OauthState = {
  isAuthInProgress: boolean;
  isAuthCompleted: boolean;
  oauthAttemptId: string | undefined;
  authTab: Window | null;
  nextDataSourceId: string | undefined;
  popupBlocked: boolean;
};

export const useOAuth = ({ event_props, onAuthorize, onError }: UseOAuthProps) => {
  const message = useMessage();

  const apiClient = useApiClient();

  const api = apiClient;

  const gasServerCtx = useGasServerContext();

  // Prefer the host-provided helper (the addon injects an ngrok-aware variant for
  // local dev); fall back to the shared util when no host override is supplied.
  const { getOAuthAuthorizationUrl: getOAuthAuthorizationUrlFromContext } = useAuthContext();
  const getOAuthAuthorizationUrl =
    getOAuthAuthorizationUrlFromContext ?? getOAuthAuthorizationUrlFallback;

  const { track } = useEventManagerContext();

  const [state, setState] = useState<OauthState>({
    isAuthInProgress: false,
    isAuthCompleted: false,
    oauthAttemptId: undefined,
    authTab: null,
    nextDataSourceId: undefined,
    popupBlocked: false,
  });

  useEffect(() => {
    return () => {
      state.authTab?.close();
    };
  }, [state.authTab]);

  const oauthAttemptMutation = useMutation({
    mutationFn: async ({
      email,
      dataSourceType,
      appInfo,
    }: {
      email: string;
      dataSourceType: DataSourceType;
      appInfo: LookerAppInfo;
    }) => {
      const ppressResponse = await apiClient.ppressRenderAuthForm({
        ppress_render_auth_form_request: {
          data_source_type: "looker",
          ppress_params: appInfo,
        },
      });

      const oauthAttempt = (
        await api.createOauthAttempt({
          create_oauth_attempt_request: {
            data_source_type: dataSourceType,
            data_source_id: undefined,
          },
        })
      ).data.oauth_attempt;

      const authorizationUrl = await getOAuthAuthorizationUrl(api, {
        email,
        oauthAttemptId: oauthAttempt?.oauth_attempt_id,
        dataSourceType,
        ppress_params: ppressResponse.data.ppress_params,
        ppress_metadata: ppressResponse.data.ppress_params,
        dataSourceId: undefined,
      });

      return { oauthAttempt, authorizationUrl };
    },
    onSuccess: ({ oauthAttempt, authorizationUrl }) => {
      const newTab = window.open(authorizationUrl, "_blank");
      const platformAttributes = gasServerCtx?.gasServer?.getPlatformAttributes?.();
      const isDesktopExcel =
        ADDON_PLATFORM === "excel" && Boolean(platformAttributes?.excel_desktop_version);

      if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
        track(AnalyticsEventNameType.AuthorizationWindowPopupBlocked, {
          data_source: DataSourceType.Looker,
          data_source_id: undefined,
        });
        if (!isDesktopExcel) {
          setState(prev => ({
            ...prev,
            popupBlocked: true,
            isAuthInProgress: false,
          }));
          return;
        }
      }

      newTab?.focus();

      setState(prev => ({
        ...prev,
        oauthAttemptId: oauthAttempt?.oauth_attempt_id,
        authTab: newTab,
        popupBlocked: false,
      }));
    },
    onError: (error: Error) => {
      state.authTab?.close();
      sentryCapture({
        error,
        screen: "AuthorizeLookerV2: OauthOverlay",
        name: "AuthorizeLookerV2: POST /api/oauth_attempt",
      });
      message.error("Error initializing connection. Try again.");
      setState(prev => ({ ...prev, isAuthInProgress: false }));
      onError?.({
        title: "Failed to initialize connection",
        message: error.message || "",
      });

      track("import_oauth_failed", {
        import_data_source: "looker",
        reason: error.message,
        looker_version: 2,
      });

      track("conn_create_failed", {
        data_source: DataSourceTypeDisplayNames.looker,
        reason: error.message,
        is_oauth_connection: true,
        looker_version: 2,
      });
    },
  });

  const { data: oauthAttemptResponse } = useQuery(
    [QueryCacheKey.OAUTH_ATTEMPT, state.oauthAttemptId],
    () => api.getOauthAttempt({ oauth_attempt_id: state.oauthAttemptId! }),
    {
      enabled: Boolean(state.isAuthInProgress && state.oauthAttemptId),
      refetchInterval: response =>
        response?.data.oauth_attempt.status !== OauthAttemptStatus.InProgress ? false : 2_000,
      refetchIntervalInBackground: true,
      retry: 3,
    }
  );

  useQuery({
    queryKey: [QueryCacheKey.DATA_SOURCE, state.nextDataSourceId],
    queryFn: () => api.getDataSource({ data_source_id: state.nextDataSourceId! }),
    enabled: Boolean(state.nextDataSourceId),
    select: response => response.data.data_source,
    onSuccess: dataSource => {
      setState(prev => ({ ...prev, isAuthInProgress: false, isAuthCompleted: true }));
      track("import_oauth_done", {
        import_data_source: "looker",
        data_source_id: dataSource.data_source_id,
        looker_version: 2,
      });
      track("conn_create_done", {
        import_data_source: "looker",
        edited_sharing: false,
        data_source_id: dataSource.data_source_id,
        is_oauth_connection: true,
        looker_version: 2,
      });
      if (dataSource) {
        onAuthorize(dataSource);
        setState(prev => ({ ...prev, isAuthCompleted: true }));
      }
    },
    retry: 3,
    onError: error => {
      setState(prev => ({ ...prev, isAuthInProgress: false, isAuthCompleted: true }));
      sentryCapture({
        error,
        name: "AuthorizeLooker: GET /api/data_source",
      });
      message.error("Error fetching data source. Try again.");
    },
  });

  useEffect(() => {
    if (oauthAttemptResponse?.data?.oauth_attempt.status === "success") {
      const dataSourceId = oauthAttemptResponse.data.oauth_attempt.data_source_id;
      setState(prev => ({
        ...prev,
        nextDataSourceId: dataSourceId,
      }));
      state.authTab?.close();
    } else if (oauthAttemptResponse?.data.oauth_attempt.status === "failure") {
      state.authTab?.close();
      setState(prev => ({ ...prev, isAuthInProgress: false }));
      message.error(
        formatOuathErrorMessage(
          oauthAttemptResponse?.data.oauth_attempt.error_message ?? "Failed to authorize."
        )
      );
      track?.("import_oauth_failed", {
        import_data_source: "looker",
        error_message: oauthAttemptResponse?.data.oauth_attempt.error_message,
      });

      track("conn_create_failed", {
        data_source: DataSourceTypeDisplayNames.looker,
        reason: oauthAttemptResponse?.data.oauth_attempt.error_message || "Failed to authorize.",
        is_oauth_connection: true,
        looker_version: 2,
      });
      onError?.({
        title: "Failed to authorize",
        message: oauthAttemptResponse?.data.oauth_attempt.error_message || "",
      });
    }
  }, [oauthAttemptResponse, onError, state.authTab, track]);

  const handleLookerAuthorize = async ({
    appInfo,
    isEmbeded,
  }: {
    appInfo: LookerAppInfo;
    isEmbeded?: boolean;
  }) => {
    setState(prev => ({ ...prev, isAuthInProgress: true, popupBlocked: false }));

    if (!isEmbeded) {
      track("conn_create_started", {
        data_source: DataSourceTypeDisplayNames.looker,
        import_data_source: "looker",
        conn_data_source: "looker",
        edited_sharing: false,
        is_oauth_connection: true,
        looker_version: 2,
      });
    }

    track("import_oauth_started", {
      import_data_source: "looker",
      conn_data_source: "looker",
      edited_sharing: false,
      looker_version: 2,
    });

    await oauthAttemptMutation.mutateAsync({
      email: event_props.email!,
      dataSourceType: "looker",
      appInfo,
    });
  };

  const handleLookerCancel = () => {
    setState(prev => ({ ...prev, isAuthInProgress: false }));
  };

  return {
    isAuthCompleted: state.isAuthCompleted,
    isAuthInProgress: state.isAuthInProgress,
    popupBlocked: state.popupBlocked,
    error: oauthAttemptMutation.error,
    handleLookerAuthorize,
    handleLookerCancel,
  };
};
